import {
  CheckOutlined,
  CloseOutlined,
  DownOutlined,
  EditOutlined,
  ExclamationCircleTwoTone,
  LockOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import {
  Button,
  DatePicker,
  Input,
  InputNumber,
  Modal,
  Space,
  Table,
  Tooltip,
} from 'antd';
import dayjs from 'dayjs';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { OpenClass } from '../../openClasses/types/OpenClass';
import {
  openClassOptionLabel,
  openClassesById,
} from '../../openClasses/utils/openClass';
import {
  ParticipantDraft,
  RegistrationDraft,
  draftHasInvalidBirthDate,
  draftMissesBirthDetails,
  draftRequiresBirthDetails,
  emptyParticipantDraft,
  isIsoCalendarDate,
  isParticipantDraftValid,
} from '../utils/openClassParticipantDraft';
import OpenClassParticipantCertificatePicker from './OpenClassParticipantCertificatePicker';
import OpenClassParticipantRegistrationsModal from './OpenClassParticipantRegistrationsModal';

type OpenClassOrderParticipantTableProps = {
  drafts: ParticipantDraft[];
  openClasses: OpenClass[];
  canCreate: boolean;
  canUpdate: boolean;
  canDelete: boolean;
  rowErrors: Record<string, string>;
  dirtyKeys: string[];
  selectedKeys: string[];
  onSelectedKeysChange: (keys: string[]) => void;
  onDraftsChange: (drafts: ParticipantDraft[]) => void;
};

const topAlignedCell = () => ({ className: 'align-top' });
const compactActionCell = () => ({ className: 'align-top px-2' });

const OpenClassOrderParticipantTable: React.FC<
  OpenClassOrderParticipantTableProps
> = ({
  drafts,
  openClasses,
  canCreate,
  canUpdate,
  canDelete,
  rowErrors,
  dirtyKeys,
  selectedKeys,
  onSelectedKeysChange,
  onDraftsChange,
}) => {
  const { t } = useTranslation();
  const [managedDraftKey, setManagedDraftKey] = useState<string>();
  const [editingKeys, setEditingKeys] = useState<string[]>([]);
  const [addCount, setAddCount] = useState(1);
  const byId = useMemo(() => openClassesById(openClasses), [openClasses]);
  const managedDraft = useMemo(
    () => drafts.find(({ key }) => key === managedDraftKey),
    [drafts, managedDraftKey],
  );

  const updateDraft = useCallback(
    (key: string, change: Partial<ParticipantDraft>) =>
      onDraftsChange(
        drafts.map((draft) =>
          draft.key === key ? { ...draft, ...change } : draft,
        ),
      ),
    [drafts, onDraftsChange],
  );

  const canEditRow = useCallback(
    (draft: ParticipantDraft) =>
      !draft.hasRecordedOutcome &&
      (draft.participantId != null ? canUpdate : canCreate),
    [canCreate, canUpdate],
  );

  const startEditing = useCallback(
    (key: string) =>
      setEditingKeys((keys) => (keys.includes(key) ? keys : [...keys, key])),
    [],
  );

  const finishEditing = useCallback(
    (key: string) =>
      setEditingKeys((keys) => keys.filter((editing) => editing !== key)),
    [],
  );

  const addRows = useCallback(() => {
    const added = Array.from({ length: addCount }, () =>
      emptyParticipantDraft(),
    );
    onDraftsChange([...drafts, ...added]);
    setEditingKeys((keys) => [...keys, ...added.map(({ key }) => key)]);
  }, [addCount, drafts, onDraftsChange]);

  useEffect(() => {
    const keysRequiringBirthDetails = drafts
      .filter(
        (draft) =>
          canEditRow(draft) &&
          (draftMissesBirthDetails(draft) || draftHasInvalidBirthDate(draft)),
      )
      .map(({ key }) => key);

    if (keysRequiringBirthDetails.length === 0) return;

    setEditingKeys((keys) => {
      const additions = keysRequiringBirthDetails.filter(
        (key) => !keys.includes(key),
      );
      return additions.length > 0 ? [...keys, ...additions] : keys;
    });
  }, [canEditRow, drafts]);

  const removeDraft = useCallback(
    (draft: ParticipantDraft) => {
      const remove = () =>
        onDraftsChange(drafts.filter(({ key }) => key !== draft.key));
      if (draft.participantId == null) {
        remove();
        return;
      }
      Modal.confirm({
        content: t(
          'views.OpenClassBookingOrderDetails.removeParticipantConfirm',
          { count: draft.registrations.length },
        ),
        okButtonProps: { danger: true },
        title: t('common.delete'),
        onOk: remove,
      });
    },
    [drafts, onDraftsChange, t],
  );

  const assignmentSummary = useCallback(
    (registrations: RegistrationDraft[]): string => {
      if (registrations.length === 0) {
        return t('views.OpenClassBookingOrderDetails.noneSet');
      }
      if (registrations.length > 1) {
        return t('views.OpenClassBookingOrderDetails.openClassCount', {
          count: registrations.length,
        });
      }
      const openClass = byId.get(registrations[0].openClassId);
      return openClass ? openClassOptionLabel(openClass) : '–';
    },
    [byId, t],
  );

  const renderOpenClassAssignment = useCallback(
    (draft: ParticipantDraft) => {
      const { registrations } = draft;
      const summary = assignmentSummary(registrations);

      return (
        <Button
          className="flex h-auto w-full min-w-0 items-center justify-between gap-2 overflow-hidden px-1 text-left"
          type="text"
          onClick={() => setManagedDraftKey(draft.key)}>
          <span
            title={summary}
            className={
              registrations.length === 0
                ? 'min-w-0 flex-1 truncate text-gray-400'
                : 'min-w-0 flex-1 truncate font-medium'
            }>
            {summary}
          </span>
          <DownOutlined className="shrink-0 text-xs text-grayMedium" />
        </Button>
      );
    },
    [assignmentSummary],
  );

  const participantColumns = useMemo(
    () => [
      {
        key: 'attendee',
        onCell: topAlignedCell,
        render: (_: unknown, draft: ParticipantDraft) => {
          const isComplete =
            draft.registrations.length > 0 && isParticipantDraftValid(draft);
          const isDirty = dirtyKeys.includes(draft.key);
          const isLocked = !!draft.hasRecordedOutcome;

          return (
            <div>
              <div className="flex items-start">
                <div className="mr-2 mt-1 flex w-4 shrink-0 justify-center">
                  {isLocked ? (
                    <Tooltip
                      title={t(
                        'views.OpenClassBookingOrderDetails.participantLockedHelp',
                      )}>
                      <LockOutlined
                        aria-label={t(
                          'views.OpenClassBookingOrderDetails.participantLocked',
                        )}
                        className="text-grayMedium"
                      />
                    </Tooltip>
                  ) : !isComplete ? (
                    <Tooltip
                      title={t(
                        'views.OpenClassBookingOrderDetails.needsInformation',
                      )}>
                      <ExclamationCircleTwoTone
                        aria-label={t(
                          'views.OpenClassBookingOrderDetails.needsInformation',
                        )}
                        twoToneColor="orange"
                      />
                    </Tooltip>
                  ) : isDirty ? (
                    <Tooltip
                      title={t(
                        'views.OpenClassBookingOrderDetails.unsavedRowShort',
                      )}>
                      <span
                        aria-label={t(
                          'views.OpenClassBookingOrderDetails.unsavedRowShort',
                        )}
                        className="mt-1 h-2 w-2 rounded-full bg-safeLifeMedium"
                        role="img"
                      />
                    </Tooltip>
                  ) : (
                    <Tooltip
                      title={t(
                        'views.OpenClassBookingOrderDetails.savedParticipant',
                      )}>
                      <CheckOutlined
                        aria-label={t(
                          'views.OpenClassBookingOrderDetails.savedParticipant',
                        )}
                        className="text-safeLifeSuccess"
                      />
                    </Tooltip>
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  {editingKeys.includes(draft.key) ? (
                    <div className="flex w-full flex-col gap-1">
                      <Input
                        placeholder={t('common.name')}
                        value={draft.name}
                        onChange={(event) =>
                          updateDraft(draft.key, { name: event.target.value })
                        }
                      />
                      <Input
                        placeholder={t('common.email')}
                        value={draft.email}
                        onChange={(event) =>
                          updateDraft(draft.key, { email: event.target.value })
                        }
                      />
                    </div>
                  ) : (
                    <div className="min-w-0">
                      <div className={draft.name ? '' : 'text-gray-400'}>
                        {draft.name || t('common.name')}
                      </div>
                      <div className="truncate text-blueGrayDark">
                        {draft.email || '–'}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              {rowErrors[draft.key] && (
                <div className="mt-1 text-xs text-red-600">
                  {rowErrors[draft.key]}
                </div>
              )}
            </div>
          );
        },
        title: t('components.OpenClassAttendeeActions.columnAttendee'),
        width: 240,
      },
      {
        key: 'birthDate',
        onCell: topAlignedCell,
        render: (_: unknown, draft: ParticipantDraft) => {
          const requiresBirthDetails = draftRequiresBirthDetails(draft);
          const isMissing =
            requiresBirthDetails && draft.birthDate.trim().length === 0;
          const isInvalid = draftHasInvalidBirthDate(draft);

          return editingKeys.includes(draft.key) ? (
            <div className="min-h-[68px]">
              <DatePicker
                aria-invalid={isMissing || isInvalid}
                aria-required={requiresBirthDetails}
                className="w-full"
                disabledDate={(date) => date.isAfter(dayjs(), 'day')}
                format="YYYY-MM-DD"
                status={isMissing || isInvalid ? 'error' : undefined}
                value={
                  isIsoCalendarDate(draft.birthDate)
                    ? dayjs(draft.birthDate)
                    : null
                }
                onChange={(date) =>
                  updateDraft(draft.key, {
                    birthDate: date ? date.format('YYYY-MM-DD') : '',
                  })
                }
              />
              <div className="mt-1 min-h-[20px] text-xs text-red-600">
                {isMissing
                  ? t(
                      'views.OpenClassBookingOrderDetails.furboBirthDateRequired',
                    )
                  : isInvalid
                    ? t(
                        'views.OpenClassBookingOrderDetails.attendeeBirthDateInvalid',
                      )
                    : null}
              </div>
            </div>
          ) : (
            <div>
              <span
                className={
                  draft.birthDate
                    ? isInvalid
                      ? 'text-red-600'
                      : ''
                    : isMissing
                      ? 'text-red-600'
                      : 'text-gray-400'
                }>
                {draft.birthDate || '–'}
              </span>
              {isMissing && (
                <div className="mt-1 text-xs text-red-600">
                  {t(
                    'views.OpenClassBookingOrderDetails.furboBirthDateRequired',
                  )}
                </div>
              )}
            </div>
          );
        },
        title: t('common.birthDate'),
        width: 150,
      },
      {
        key: 'birthPlace',
        onCell: topAlignedCell,
        render: (_: unknown, draft: ParticipantDraft) => {
          const requiresBirthDetails = draftRequiresBirthDetails(draft);
          const isMissing =
            requiresBirthDetails && draft.birthPlace.trim().length === 0;

          return editingKeys.includes(draft.key) ? (
            <div className="min-h-[68px]">
              <Input
                aria-invalid={isMissing}
                aria-required={requiresBirthDetails}
                placeholder={t('common.birthPlace')}
                status={isMissing ? 'error' : undefined}
                value={draft.birthPlace}
                onChange={(event) =>
                  updateDraft(draft.key, { birthPlace: event.target.value })
                }
              />
              <div className="mt-1 min-h-[20px] text-xs text-red-600">
                {isMissing
                  ? t(
                      'views.OpenClassBookingOrderDetails.furboBirthPlaceRequired',
                    )
                  : null}
              </div>
            </div>
          ) : (
            <div>
              <span
                className={
                  draft.birthPlace
                    ? ''
                    : isMissing
                      ? 'text-red-600'
                      : 'text-gray-400'
                }>
                {draft.birthPlace || '–'}
              </span>
              {isMissing && (
                <div className="mt-1 text-xs text-red-600">
                  {t(
                    'views.OpenClassBookingOrderDetails.furboBirthPlaceRequired',
                  )}
                </div>
              )}
            </div>
          );
        },
        title: t('common.birthPlace'),
        width: 150,
      },
      {
        key: 'certificates',
        onCell: topAlignedCell,
        render: (_: unknown, draft: ParticipantDraft) => (
          <OpenClassParticipantCertificatePicker
            canUpdate={canEditRow(draft)}
            draft={draft}
            openClasses={openClasses}
            onChange={(
              registrationKey,
              certificateTemplateId,
              certificateType,
            ) =>
              updateDraft(draft.key, {
                registrations: draft.registrations.map((registration) =>
                  registration.key === registrationKey
                    ? {
                        ...registration,
                        certificateTemplateId,
                        certificateType,
                      }
                    : registration,
                ),
              })
            }
          />
        ),
        title: t('views.OpenClasses.certificateType'),
        width: 140,
      },
      {
        key: 'openClass',
        onCell: topAlignedCell,
        render: (_: unknown, draft: ParticipantDraft) =>
          renderOpenClassAssignment(draft),
        title: t('views.OpenClassBookingOrderDetails.mapToOpenClass'),
        width: 200,
      },
      {
        key: 'actions',
        onCell: compactActionCell,
        render: (_: unknown, draft: ParticipantDraft) => (
          <Space size={0}>
            {canEditRow(draft) &&
              (editingKeys.includes(draft.key) ? (
                <Button
                  aria-label={t(
                    'views.OpenClassBookingOrderDetails.doneEditing',
                  )}
                  disabled={
                    draftMissesBirthDetails(draft) ||
                    draftHasInvalidBirthDate(draft)
                  }
                  size="small"
                  type="text"
                  onClick={() => finishEditing(draft.key)}>
                  {t('views.OpenClassBookingOrderDetails.doneEditingShort')}
                </Button>
              ) : (
                <Button
                  aria-label={t('common.edit')}
                  icon={<EditOutlined />}
                  size="small"
                  type="text"
                  onClick={() => startEditing(draft.key)}
                />
              ))}
            {!draft.hasRecordedOutcome &&
              (draft.participantId == null ? canCreate : canDelete) && (
                <Button
                  aria-label={t(
                    'views.OpenClassBookingOrderDetails.removeAttendee',
                  )}
                  icon={<CloseOutlined />}
                  size="small"
                  type="text"
                  onClick={() => removeDraft(draft)}
                />
              )}
          </Space>
        ),
        title: '',
        width: 100,
      },
    ],
    [
      canCreate,
      canDelete,
      canEditRow,
      dirtyKeys,
      editingKeys,
      finishEditing,
      openClasses,
      removeDraft,
      renderOpenClassAssignment,
      rowErrors,
      startEditing,
      t,
      updateDraft,
    ],
  );

  return (
    <>
      <Table
        bordered
        columns={participantColumns}
        dataSource={drafts}
        locale={{ emptyText: <div className="h-10" /> }}
        pagination={false}
        rowKey="key"
        tableLayout="fixed"
        rowClassName={(draft) =>
          draft.registrations.length === 0 || !isParticipantDraftValid(draft)
            ? 'open-class-order-incomplete-row'
            : ''
        }
        rowSelection={
          canCreate
            ? {
                getCheckboxProps: (draft) => ({
                  disabled: !!draft.hasRecordedOutcome,
                }),
                selectedRowKeys: selectedKeys,
                onChange: (keys) => onSelectedKeysChange(keys.map(String)),
              }
            : undefined
        }
      />
      {canCreate && (
        <Space className="mt-3">
          <Button icon={<PlusOutlined />} type="dashed" onClick={addRows}>
            {t('views.OpenClasses.addAttendee')}
          </Button>
          <InputNumber
            aria-label={t(
              'views.OpenClassBookingOrderDetails.addAttendeeCount',
            )}
            className="w-16"
            min={1}
            max={100}
            value={addCount}
            onChange={(value) => setAddCount(value ?? 1)}
          />
        </Space>
      )}
      {managedDraft && (
        <OpenClassParticipantRegistrationsModal
          canCreate={canCreate && !managedDraft.hasRecordedOutcome}
          canDelete={canDelete && !managedDraft.hasRecordedOutcome}
          canUpdate={canUpdate && !managedDraft.hasRecordedOutcome}
          draft={managedDraft}
          open
          openClasses={openClasses}
          onChange={(registrations: RegistrationDraft[]) =>
            updateDraft(managedDraft.key, { registrations })
          }
          onClose={() => setManagedDraftKey(undefined)}
        />
      )}
    </>
  );
};

export default OpenClassOrderParticipantTable;
